import { useTranslation } from "react-i18next";
import type { ScheduledJobPublishPreview } from "../lib/scheduled-job-preview";
import { hasEventTypeAttribute } from "../lib/scheduled-job-preview";

interface ScheduledJobPreviewProps {
  preview: ScheduledJobPublishPreview | null;
  /** When true, surface a missing-eventType warning before submit (AE2). */
  warnMissingEventType?: boolean;
}

/**
 * Live read-out of topic + attributes (+ body summary) before create/update
 * submit. Server-built targets remain the source of truth; this is orientation.
 */
export function ScheduledJobPreview({
  preview,
  warnMissingEventType = false,
}: ScheduledJobPreviewProps) {
  const { t } = useTranslation();

  if (!preview) {
    return (
      <p className="text-xs text-muted-foreground">
        {t(
          "admin.scheduledJobs.preview.empty",
          "Pick a triggerable event to preview the Pub/Sub target.",
        )}
      </p>
    );
  }

  const attributeEntries = Object.entries(preview.attributes);
  const showEventTypeWarning =
    warnMissingEventType && !hasEventTypeAttribute(preview.attributes);

  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 px-3 py-2.5"
      data-testid="scheduled-job-preview"
    >
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t("admin.scheduledJobs.preview.title", "Publish preview")}
      </span>
      <dl className="m-0 flex flex-col gap-1.5">
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-muted-foreground">
            {t("admin.scheduledJobs.preview.topic", "Topic")}
          </dt>
          <dd className="m-0 break-all font-mono text-[11px] text-foreground">{preview.topic}</dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-muted-foreground">
            {t("admin.scheduledJobs.preview.eventType", "eventType")}
          </dt>
          <dd className="m-0 break-all font-mono text-[11px] text-foreground">
            {preview.eventType ??
              t("admin.scheduledJobs.preview.eventTypeMissing", "(missing)")}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-[11px] text-muted-foreground">
            {t("admin.scheduledJobs.preview.attributes", "Attributes")}
          </dt>
          <dd className="m-0">
            {attributeEntries.length === 0 ? (
              <span className="font-mono text-[11px] text-muted-foreground">
                {t("admin.scheduledJobs.preview.attributesEmpty", "(none)")}
              </span>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                {attributeEntries.map(([key, value]) => (
                  <li key={key} className="break-all font-mono text-[11px] text-foreground">
                    {key}={value}
                  </li>
                ))}
              </ul>
            )}
          </dd>
        </div>
        {preview.bodySummary ? (
          <div className="flex flex-col gap-0.5">
            <dt className="text-[11px] text-muted-foreground">
              {t("admin.scheduledJobs.preview.body", "Body")}
            </dt>
            <dd className="m-0 break-all font-mono text-[11px] text-foreground">
              {preview.bodySummary}
            </dd>
          </div>
        ) : null}
      </dl>
      {showEventTypeWarning ? (
        <p
          className="rounded-md bg-warning-subtle px-2 py-1.5 text-[11px] text-warning-subtle-foreground"
          data-testid="scheduled-job-preview-event-type-warning"
        >
          {t(
            "admin.scheduledJobs.preview.eventTypeWarning",
            "attributes.eventType is missing. The server will reject this save.",
          )}
        </p>
      ) : null}
    </div>
  );
}
